/**
 * Why the field of four hundred contracts became a list of thirty.
 *
 * The Overview shows the top eight and the two market gauges. Nothing showed
 * the selection itself: how many contracts the venue lists, how many this
 * strategy was willing to look at, how many it paid to measure, how many
 * survived the filters, and how many are unavailable right now because a slot
 * already holds them or they are cooling down.
 *
 * That funnel is the answer to "why is it not trading X", and until now it was
 * only answerable by reading a worker's journal.
 *
 * What this screen cannot show, and says so: the measured contracts as a list.
 * The published window stores the counts and the ranked instruments — every
 * one of them, not the thirty it once held — so a table of the whole venue
 * would be hundreds of rows this system never wrote down. The front of the
 * queue stands open and the rest is one click away, because a ranking whose
 * bottom no slot will reach is not what a reader came to look at.
 *
 * Nothing here decides anything. Opening this page asks the exchange nothing
 * and changes no selection: every figure was computed when the strategy last
 * looked at the market.
 */
import { ArrowDown, ArrowUp } from "lucide-react";
import { Stat } from "@/design-system/Stat";
import { Button } from "@/design-system/Button";

/**
 * What this screen is, written once. The sentence under a page title does not
 * move with the data — a line that changed with the account would be a figure,
 * and figures belong in the strip below it.
 */
const SUBTITLE =
  "Оценка инструментов по Skryptos Score: что прошло отбор, что отсеяно и почему.";
import { useState } from "react";

import { PageHeader } from "@/design-system/PageHeader";
import { Panel } from "@/design-system/Panel";
import { InstrumentSymbol } from "@/design-system/InstrumentSymbol";
import { Score } from "@/design-system/Score";
import { ErrorState, Forbidden, Loading } from "@/design-system/states/States";
import { StatusBadge, type BadgeTone } from "@/design-system/StatusBadge";
import type {
  RankingCandidateResponse,
  RankingRefusalResponse,
  RankingResponse,
} from "@/shared/api";
import { compactMoney, compareDecimal, money, percent } from "@/shared/money/money";
import { humanAge } from "@/shared/time/age";

import {
  VERDICT_LABEL,
  VERDICT_TONE,
  availabilityOf,
  evidenceLabel,
  reasonLabel,
  refusalLabel,
  scoreOf,
} from "@/shared/strategy/score";
import { plural } from "@/shared/text/plural";

import { ScoreBreakdown } from "@/shared/strategy/ScoreBreakdown";
import { useScanner } from "./useScanner";

/**
 * The states a contract can be in, in this screen's own words.
 *
 * Spelled out here rather than shared with the compact panel on the Overview:
 * that one has room for a badge, this one explains why a contract is out and
 * for how long. The same word in two places with different amounts of detail
 * is not duplication — the same *rule* in two places would be.
 */
const STATUS: Record<string, { readonly label: string; readonly tone: BadgeTone }> = {
  READY: { label: "Готова", tone: "ready" },
  ACTIVE: { label: "В работе", tone: "muted" },
  COOLDOWN: { label: "Отдыхает", tone: "warning" },
  QUARANTINED: { label: "В карантине", tone: "danger" },
  BLOCKED: { label: "Заблокирована", tone: "danger" },
  // The guard closed the entry. A high score with the entry shut is a normal
  // state of a normal instrument, so it warns rather than alarms.
  ENTRY_BLOCKED: { label: "Вход закрыт", tone: "warning" },
};

type Column = "rank" | "symbol" | "score" | "turnover" | "spread" | "pullback" | "recovery";
type Sort = { readonly column: Column; readonly order: "asc" | "desc" };

const BY_RANK: Sort = { column: "rank", order: "asc" };

/**
 * How much of the queue stands open by default.
 *
 * Thirty, which is what the window used to hold and what a person reads before
 * deciding they have seen enough. Below it a contract is ranked and available
 * to a future choice, which is worth keeping reachable and not worth putting
 * in front of somebody every time they open the screen.
 */
const FRONT = 30;

/**
 * One input of the score, as the pass recorded it.
 *
 * BASE v2 publishes the features it scored on and not the flat measurements
 * BASE v1 published, so a table written for v1 printed a dash in four columns
 * of every row. These are the same facts under their own names.
 */
function feature(one: RankingCandidateResponse, name: string): string | null {
  return (
    one.contributions?.find((item) => item.feature === name)?.raw_value ?? null
  );
}

/** A rate, or a dash when the pass did not record one. */
function percentOrDash(value: string | null): string {
  return value === null ? "—" : percent(value, 1);
}

/** A bounded feature that is not a rate — shown as measured, two decimals. */
function ratioOrDash(value: string | null): string {
  return value === null ? "—" : money(value);
}

/** A count of bars. Whole, because half a bar was never observed. */
function barsOrDash(value: string | null): string {
  return value === null ? "—" : money(value, 0);
}

/** The pools BASE v2 scores an instrument in. */
type LogicalPool = NonNullable<RankingCandidateResponse["logical_pool"]>;

function value(one: RankingCandidateResponse, column: Column): string {
  if (column === "symbol") return one.symbol;
  if (column === "score") return one.final_score ?? String(one.skryptos_score ?? "");
  if (column === "turnover")
    return one.turnover ?? feature(one, "RECOVERY_SPEED") ?? "";
  if (column === "spread")
    return one.spread ?? feature(one, "DECLINE_STABILITY") ?? "";
  if (column === "pullback")
    return one.pullback_rate ?? feature(one, "PULLBACK_FREQUENCY") ?? "";
  if (column === "recovery")
    return one.recovery_rate ?? feature(one, "RECOVERY_RATE") ?? "";
  return String(one.rank ?? 9999);
}

function compare(
  left: RankingCandidateResponse,
  right: RankingCandidateResponse,
  sort: Sort,
): number {
  const a = value(left, sort.column);
  const b = value(right, sort.column);
  const numeric = sort.column !== "symbol";
  const decided = numeric ? compareDecimal(a, b) : a.localeCompare(b);
  return sort.order === "asc" ? decided : -decided;
}

export function ScannerPage({
  market,
}: {
  /** The market block, composed by the app: it belongs to another feature. */
  readonly market: React.ReactNode;
}): React.JSX.Element {
  const [sort, setSort] = useState<Sort>(BY_RANK);
  // Which pool's ranking is on screen. BASE v2 scores every instrument twice —
  // once for PRIME and once for PULSE — so the payload holds two ranked lists,
  // and showing them as one produced two rows called #1 and a column of scores
  // that did not descend.
  const [pool, setPool] = useState<LogicalPool | null>(null);
  const [deep, setDeep] = useState(false);
  const state = useScanner();

  if (state.status === "loading") return <Loading title="Читаю отбор…" />;
  if (state.status === "forbidden") return <Forbidden />;
  if (state.status === "unreadable") {
    return (
      <ErrorState
        title="Не могу прочитать отбор"
        detail="Данные не пришли. Позиции и ордера на бирже от этого экрана не зависят."
      />
    );
  }

  const ranking = state.ranking;

  function order(column: Column): void {
    setSort((was) =>
      was.column === column
        ? { column, order: was.order === "desc" ? "asc" : "desc" }
        : { column, order: column === "symbol" || column === "rank" ? "asc" : "desc" },
    );
  }

  if (ranking.state === "NO_DATA_YET") {
    return (
      <div className="flex flex-col gap-2">
        <PageHeader title="Рейтинг" description={SUBTITLE} />
        <Panel title="Отбор" testId="scanner-empty">
          <p className="text-body-main text-text-muted">
            Рейтинг ещё не рассчитан. Он появится, как только стратегия первый
            раз посмотрит на рынок.
          </p>
        </Panel>
      </div>
    );
  }

  const pools = [
    ...new Set(
      ranking.top_candidates
        .map((one) => one.logical_pool)
        .filter((one): one is LogicalPool => one != null),
    ),
  ].sort();
  const shown = pools.length > 1 ? (pool ?? pools[0] ?? null) : null;
  const candidates = [...ranking.top_candidates]
    .filter((one) => shown === null || one.logical_pool === shown)
    .sort((left, right) => compare(left, right, sort));
  // The front of the queue by default, not the whole of it. The window used to
  // hold thirty and now holds every ranked instrument — a hundred and eleven on
  // the live pass, scoring from 79.6 down to 35.4 — and the bottom of that list
  // is not a thing a slot will reach. Opening on all of it makes the reader
  // scroll past what will be taken to find out that nothing else will be.
  //
  // Available in full on one click, because this is the screen where the whole
  // ranking lives and a screen that hid it would leave it living nowhere.
  const visible = deep ? candidates : candidates.slice(0, FRONT);
  const beyond = candidates.length - visible.length;

  return (
    <div className="flex flex-col gap-2" data-testid="scanner-page">
      <PageHeader title="Рейтинг" description={SUBTITLE} />

      {/* The pass and the market it ran against, side by side. The gauges used
          to hold a column beside the candidate table, which is thirty rows
          long: below the tenth row that column was empty for the height of a
          screen. Both blocks describe the same pass, and both are short. */}
      <div className="grid grid-cols-1 gap-2 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-8">
          <Funnel ranking={ranking} />
        </div>
        <div className="min-w-0 xl:col-span-4">{market}</div>
      </div>

      <div className="min-w-0">
        <Refused refusals={ranking.refusals ?? []} />
      </div>

      <div className="min-w-0">
        <Panel
          // The whole queue, not the part standing open. A count that shrank
          // when the list collapsed would say the ranking got smaller.
          title={`Кандидаты · ${String(candidates.length)}`}
          testId="scanner-candidates"
          trailing={
            <span className="flex items-center gap-2">
              {pools.length > 1 && (
                <span
                  role="radiogroup"
                  aria-label="Пул рейтинга"
                  className="flex gap-0.5"
                  data-testid="ranking-pools"
                >
                  {pools.map((one) => (
                    <Button
                      key={one}
                      variant="ghost"
                      size="sm"
                      role="radio"
                      aria-checked={one === shown}
                      selected={one === shown}
                      data-testid={`ranking-pool-${one}`}
                      onClick={() => {
                        setPool(one);
                      }}
                    >
                      {one}
                    </Button>
                  ))}
                </span>
              )}
            </span>
          }
          flush
        >
          <div className="@container">
            <Head sort={sort} onSort={order} flat={candidates.some((one) => one.turnover != null)} />
            <ul>
              {visible.map((one) => (
                <Row key={`${one.symbol}:${one.logical_pool ?? ""}`} candidate={one} />
              ))}
            </ul>
            {(beyond > 0 || deep) && (
              <Button
                variant="ghost"
                aria-expanded={deep}
                data-testid="scanner-open-deep"
                onClick={() => {
                  setDeep((was) => !was);
                }}
                className="min-h-9 w-full gap-1 rounded-none border-t border-border-subtle text-action-primary hover:text-action-hover pointer-coarse:min-h-11"
              >
                {deep
                  ? "Свернуть до начала очереди"
                  : `Показать весь рейтинг · ещё ${String(beyond)}`}
              </Button>
            )}
            {ranking.below_the_fold > 0 && (
              // The window keeps thirty; the pass ranked more. Said plainly,
              // because a list that ends without explanation reads as the end
              // of the field rather than the end of what was published.
              <p
                className="border-t border-border-subtle px-3 py-2 text-helper-sm text-text-muted lg:px-4"
                data-testid="scanner-below"
              >
                {`Ещё ${String(ranking.below_the_fold)} ${plural(ranking.below_the_fold, "контракт", "контракта", "контрактов")} ${plural(ranking.below_the_fold, "прошёл", "прошли", "прошли")} отбор, но в окно не ${plural(ranking.below_the_fold, "попал", "попали", "попали")} — публикуются только первые ${String(candidates.length)}.`}
              </p>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

/**
 * The field, stage by stage, with what each stage removed.
 *
 * A count on its own says nothing: "измерено 482" is only meaningful beside
 * how many there were before it and how many survived after. The drop is the
 * information, so the drop is what is shown.
 */
function Funnel({ ranking }: { readonly ranking: RankingResponse }): React.JSX.Element {
  const stages: {
    readonly key: string;
    readonly label: string;
    readonly count: number | null;
    readonly hint: string;
  }[] = [
    {
      key: "listed",
      label: "Торгуется на бирже",
      count: ranking.listed_count,
      hint: "Сколько бессрочных контрактов биржа отдала как торгуемые",
    },
    {
      key: "considered",
      label: "Взято в рассмотрение",
      count: ranking.considered_count,
      hint: "Прошли правила допуска: оборот, спред, возраст листинга",
    },
    {
      key: "measured",
      label: "Измерено",
      count: ranking.measured_count,
      hint: "Каждое измерение — запрос свечей, поэтому меряются не все подряд",
    },
    {
      key: "ranked",
      label: "В рейтинге",
      // A stage is gone from here, and it was not carrying anything: "прошли
      // фильтры" counted the selectable instruments and "в рейтинге" counted
      // the instruments, and every published instrument is selectable — so the
      // two were the same number by construction, on every pass, for ever. A
      // funnel step that never removes anything teaches a reader to stop
      // reading the steps.
      //
      // The drop it hid is the one before it: measured to ranked, seventy
      // instruments on the pass this was written against, and what removed
      // them is named in the block below rather than in a stage with no name
      // of its own.
      count: ranking.ranked_count,
      hint: "Получили оценку и место. Что отсеялось перед этим — в блоке «Не попали в рейтинг»",
    },
  ];

  return (
    <Panel
      title="Как отбирались контракты"
      testId="scanner-funnel"
      trailing={
        ranking.age_seconds === null ? undefined : (
          <span className="text-helper-sm text-text-muted" data-testid="scanner-age">
            {`проход ${humanAge(ranking.age_seconds)} назад`}
          </span>
        )
      }
    >
      <div className="flex flex-col gap-2">
        {/* Three shared rows, so a two-line label does not push its own number
            below the four beside it. The stages read across — 735, 732, 254 —
            and they can only be read across if they sit on one line. */}
        <ol className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
          {stages.map((stage, index) => (
            <Stage
              key={stage.key}
              stage={stage}
              // What the previous stage held, so this one can say what it cost.
              before={index === 0 ? null : stages[index - 1]?.count ?? null}
            />
          ))}
        </ol>


        <Availability ranking={ranking} />
      </div>
    </Panel>
  );
}

function Stage({
  stage,
  before,
}: {
  readonly stage: {
    readonly key: string;
    readonly label: string;
    readonly count: number | null;
    readonly hint: string;
  };
  readonly before: number | null;
}): React.JSX.Element {
  const dropped =
    before === null || stage.count === null ? null : before - stage.count;

  return (
    <li className="min-w-0">
      <Stat
        title={stage.hint}
        testId={`stage-${stage.key}`}
        label={stage.label}
        className="h-full"
        // Unknown is not zero: a pass that never recorded the size of the
        // venue did not observe an empty one.
        value={stage.count === null ? "—" : stage.count}
        note={
          dropped !== null && dropped > 0 ? `отсеяно ${String(dropped)}` : undefined
        }
      />
    </li>
  );
}

/**
 * Every instrument the pass looked at and would not rank, with the reason.
 *
 * This screen's stated job is to answer "why is it not trading X", and it
 * could not answer it for any X the strategy had declined — because a declined
 * instrument was dropped before publication and simply was not on the page.
 * The worst case was the fifty the account is holding: a held symbol is
 * refused as ALREADY_HELD before it is ever scored, so it vanished from the
 * ranking while the line below printed "в работе 0" beside the gap.
 *
 * Grouped by reason and counted, with the symbols behind each group one click
 * away. Six hundred refusals listed flat is not an answer, it is a second
 * table; a count per reason is the shape of the question a person actually
 * asks, and the symbols are there when they are asking about one.
 *
 * Held instruments lead and carry their slot, because that is the group the
 * owner comes here for and "held by something" is not actionable.
 */
function Refused({
  refusals,
}: {
  readonly refusals: readonly RankingRefusalResponse[];
}): React.JSX.Element | null {
  const [open, setOpen] = useState<string | null>(null);
  // Absent, not empty. A window published before the pass carried refusals
  // says nothing about them, and an empty block would report that the strategy
  // declined nothing — which of seven hundred contracts it never does.
  if (refusals.length === 0) return null;

  const groups = new Map<string, RankingRefusalResponse[]>();
  for (const one of refusals) {
    const bucket = groups.get(one.reason);
    if (bucket === undefined) groups.set(one.reason, [one]);
    else bucket.push(one);
  }
  const ordered = [...groups.entries()].sort(([left, ones], [right, others]) =>
    left === HELD
      ? -1
      : right === HELD
        ? 1
        : others.length - ones.length || left.localeCompare(right),
  );

  // The funnel's last drop is "measured minus ranked", and these are not all of
  // that drop: a held instrument is refused before measurement, so it is on
  // this list without ever having been in the stage the drop is taken from.
  // Said plainly, because a reader who adds the counts and compares them with
  // the funnel is doing the right thing and must not be told the arithmetic is
  // broken.
  const unmeasured = refusals.filter((one) => !one.measured).length;

  return (
    <Panel title="Не попали в рейтинг" testId="scanner-refused" flush>
      <ul>
        {ordered.map(([reason, ones]) => {
          const shown = open === reason;
          return (
            <li key={reason} className="border-b border-border-subtle last:border-b-0">
              <Button
                variant="ghost"
                aria-expanded={shown}
                data-testid={`refusal-${reason}`}
                onClick={() => {
                  setOpen((was) => (was === reason ? null : reason));
                }}
                className="w-full justify-between rounded-none px-3 py-2 text-left lg:px-4"
              >
                <span className="min-w-0 text-body-main text-text-primary">
                  {refusalLabel(reason)}
                </span>
                <span className="font-numeric tabular-nums text-text-secondary">
                  {ones.length}
                </span>
              </Button>
              {shown && (
                <ul
                  data-testid={`refusal-symbols-${reason}`}
                  className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border-subtle px-3 py-2 lg:px-4"
                >
                  {[...ones]
                    .sort((left, right) => left.symbol.localeCompare(right.symbol))
                    .map((one) => (
                      <li
                        key={one.symbol}
                        className="flex items-baseline gap-1.5 font-mono text-helper-sm"
                      >
                        <span className="text-text-secondary">{one.symbol}</span>
                        {one.slot_id !== null && (
                          <span className="text-text-muted">{one.slot_id}</span>
                        )}
                      </li>
                    ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      {unmeasured > 0 && (
        <p
          className="border-t border-border-subtle px-3 py-2 text-helper-sm text-text-muted lg:px-4"
          data-testid="refused-unmeasured"
        >
          {`Из них ${String(unmeasured)} ${plural(unmeasured, "не измерялся", "не измерялись", "не измерялись")} — занятый инструмент отбраковывается до измерения, поэтому он не входит в отсев на шаге «Измерено → В рейтинге».`}
        </p>
      )}
    </Panel>
  );
}

/** The survey's word for "a slot already has it", named once. */
const HELD = "ALREADY_HELD";

/** Why a contract that passed the filters is still not available now. */
function Availability({
  ranking,
}: {
  readonly ranking: RankingResponse;
}): React.JSX.Element {
  const parts: [string, number, string][] = [
    ["свободны", ranking.ready_count, "Слот может взять их прямо сейчас"],
    [
      "в работе",
      ranking.active_count,
      "Уже держат слот — одна монета, один слот. Список — в блоке «Не попали в рейтинг»",
    ],
    ["отдыхают", ranking.cooldown_count, "Недавно закрылись, ждут окончания паузы"],
    ["в карантине", ranking.quarantined_count, "Отложены после отказов"],
    ["заблокированы", ranking.blocked_count, "Не проходят по правилам допуска"],
  ];

  return (
    <p
      className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-helper-sm text-text-secondary"
      data-testid="scanner-availability"
    >
      {parts.map(([label, count, hint]) => (
        <span key={label} title={hint}>
          {label}{" "}
          <span className="font-numeric tabular-nums text-text-primary">{count}</span>
        </span>
      ))}
    </p>
  );
}

const COLUMNS =
  "grid w-full grid-cols-[2.5rem_minmax(0,1fr)_4rem] items-center gap-x-3 gap-y-1 px-3 lg:px-4" +
  " @md:grid-cols-[2.5rem_minmax(8rem,1.4fr)_minmax(7rem,1fr)_4rem]" +
  " @3xl:grid-cols-[2.5rem_minmax(9rem,1.1fr)_minmax(14rem,1.7fr)_repeat(4,minmax(6rem,1fr))_minmax(5rem,auto)]";


const RANK_CELL = "col-start-1 row-start-1";
const SYMBOL_CELL = "col-start-2 row-start-1";
const STATUS_CELL = "col-start-2 row-start-2 @md:row-start-1 @md:col-start-3";
const TURNOVER_CELL = "@max-3xl:hidden @3xl:col-start-4";
const SPREAD_CELL = "@max-3xl:hidden @3xl:col-start-5";
const PULLBACK_CELL = "@max-3xl:hidden @3xl:col-start-6";
const RECOVERY_CELL = "@max-3xl:hidden @3xl:col-start-7";
const SCORE_CELL = "col-start-3 row-start-1 @md:col-start-4 @3xl:col-start-8";

function Head({
  sort,
  onSort,
  flat,
}: {
  readonly sort: Sort;
  readonly onSort: (column: Column) => void;
  /** Whether the pass published the flat measurements BASE v1 published. */
  readonly flat: boolean;
}): React.JSX.Element {
  return (
    <div
      data-testid="scanner-heads"
      className={`${COLUMNS} sticky top-0 z-10 border-b border-border-subtle bg-surface-default py-1.5`}
    >
      <Sortable column="rank" sort={sort} onSort={onSort} className={RANK_CELL}>
        #
      </Sortable>
      <Sortable column="symbol" sort={sort} onSort={onSort} className={SYMBOL_CELL}>
        Инструмент
      </Sortable>
      <span className={`${STATUS_CELL} text-label-caps text-text-secondary`}>
        Доступность
      </span>
      <Sortable
        column="turnover"
        sort={sort}
        onSort={onSort}
        className={TURNOVER_CELL}
        right
        title={
          flat
            ? "Оборот за сутки в USDT. Порог допуска отсекает тонкие рынки."
            : "Скорость восстановления — за сколько баров откат закрывается."
        }
      >
        {flat ? "Оборот" : "Скорость"}
      </Sortable>
      <Sortable
        column="spread"
        sort={sort}
        onSort={onSort}
        className={SPREAD_CELL}
        right
        title={
          flat
            ? "Спред между лучшими ценами. Широкий спред съедает результат цикла."
            : "Стабильность падения — насколько ровно инструмент идёт вниз."
        }
      >
        {flat ? "Спред" : "Стабильность"}
      </Sortable>
      <Sortable
        column="pullback"
        sort={sort}
        onSort={onSort}
        className={PULLBACK_CELL}
        right
        title="Как часто цена откатывается — то, на чём BASE v1 входит."
      >
        Откат
      </Sortable>
      <Sortable
        column="recovery"
        sort={sort}
        onSort={onSort}
        className={RECOVERY_CELL}
        right
        title="Как часто откат восстанавливается — то, на чём BASE v1 выходит."
      >
        Возврат
      </Sortable>
      <Sortable column="score" sort={sort} onSort={onSort} className={SCORE_CELL} right>
        Оценка
      </Sortable>
    </div>
  );
}

function Sortable({
  column,
  sort,
  onSort,
  className = "",
  right = false,
  title,
  children,
}: {
  readonly column: Column;
  readonly sort: Sort;
  readonly onSort: (column: Column) => void;
  readonly className?: string;
  readonly right?: boolean;
  readonly title?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const active = sort.column === column;
  const Arrow = sort.order === "desc" ? ArrowDown : ArrowUp;

  return (
    <Button
      variant="ghost"
      size="cell"
      data-testid={`sort-${column}`}
      data-active={active ? "true" : "false"}
      title={title}
      onClick={() => {
        onSort(column);
      }}
      className={[
        className,
        "pointer-coarse:min-h-11",
        right ? "justify-end" : "",
        active ? "font-medium text-text-primary" : "",
      ].join(" ")}
    >
      {children}
      {active && <Arrow aria-hidden="true" className="size-3 shrink-0" />}
    </Button>
  );
}

function Row({
  candidate,
}: {
  readonly candidate: RankingCandidateResponse;
}): React.JSX.Element {
  // Read from the payload rather than the generated type: the score version is
  // in the schema, the evidence level, the decomposition and the entry regime
  // are not. Nothing is invented — a pool least of all, because this screen
  // scores instruments and the pool is decided when a slot is opened.
  const reading = scoreOf(candidate);
  // The guard has the last word: `admission_allowed` requires it to pass, so a
  // badge reading "Готова" on a blocked instrument is not a nuance beside the
  // verdict — it is the opposite of what the strategy will do.
  const available = availabilityOf(
    candidate.status,
    reading.regime?.verdict ?? null,
    reading.regime?.reason,
  );
  const status = STATUS[available.status] ?? {
    label: available.label,
    tone: "muted" as BadgeTone,
  };

  return (
    <li
      className={`${COLUMNS} min-h-11 border-b border-border-subtle py-1.5 last:border-b-0 @md:min-h-9`}
      data-testid={`candidate-${candidate.symbol}`}
    >
      <span className={`${RANK_CELL} font-mono text-helper-sm text-text-muted`}>
        {candidate.rank === null ? "—" : `#${String(candidate.rank)}`}
      </span>

      <span className={`${SYMBOL_CELL} min-w-0`}>
        <InstrumentSymbol symbol={candidate.symbol} />
        <span className="block text-helper-sm text-text-muted">
          {candidate.logical_pool ?? ""}
        </span>
      </span>

      <span className={`${STATUS_CELL} flex flex-wrap items-center gap-1.5`}>
        <StatusBadge tone={status.tone}>
          {status.label}
        </StatusBadge>
        {/* How much history stands behind the score — a separate fact from the
            score, and it used to sit under it. Three phrases of three widths,
            right-aligned beneath a column of figures, left that column ragged
            while the middle of the row stood empty. It is a category, so §4.1
            gives it a chip, and the chip goes where the row has room. */}
        {evidenceLabel(reading.evidence_level) !== null && (
          <StatusBadge
            tone="muted"
            shape="outlined"
            testId={`score-evidence-${candidate.symbol}`}
          >
            {evidenceLabel(reading.evidence_level)}
          </StatusBadge>
        )}
        {/* Why it is out, and for how long. The Overview has room for the word
            only; this screen exists to carry the rest. */}
        {candidate.active_slot_id !== null && (
          <span className="font-mono text-helper-sm text-text-muted">
            {candidate.active_slot_id}
          </span>
        )}
        {candidate.cooldown_remaining_seconds !== null && (
          <span className="text-helper-sm text-text-muted">
            {`ещё ${humanAge(candidate.cooldown_remaining_seconds)}`}
          </span>
        )}
        {candidate.quarantine_reason !== null && (
          <span className="text-helper-sm text-status-negative">
            {candidate.quarantine_reason}
          </span>
        )}
        {/* Whether a long may be opened right now — a different question from
            how good the instrument is. A high score with the entry blocked is
            a normal state, so the verdict sits beside the status rather than
            dimming the score.

            Absent once the status has absorbed it: "Вход закрыт" beside a
            second badge also reading "вход закрыт" is the same sentence twice,
            and the reason under it already says which. It stays for PASS and
            UNKNOWN, which the status does not carry. */}
        {reading.regime !== null && available.status !== "ENTRY_BLOCKED" && (
          <StatusBadge
            tone={VERDICT_TONE[reading.regime.verdict]}
            shape="outlined"
            testId={`entry-regime-${candidate.symbol}`}
          >
            {VERDICT_LABEL[reading.regime.verdict]}
          </StatusBadge>
        )}
        {reading.regime?.reason != null && (
          <span
            className="text-helper-sm text-text-muted"
            data-testid={`entry-reason-${candidate.symbol}`}
          >
            {reasonLabel(reading.regime.reason)}
          </span>
        )}
      </span>

      <span
        data-column="turnover"
        className={`${TURNOVER_CELL} text-right text-helper-sm tabular-nums text-text-secondary`}
        title={candidate.turnover ?? undefined}
      >
        {candidate.turnover != null
          ? compactMoney(candidate.turnover)
          : barsOrDash(feature(candidate, "RECOVERY_SPEED"))}
      </span>

      <span
        data-column="spread"
        className={`${SPREAD_CELL} text-right text-helper-sm tabular-nums text-text-secondary`}
      >
        {candidate.spread != null
          ? percent(candidate.spread, 2)
          : ratioOrDash(feature(candidate, "DECLINE_STABILITY"))}
      </span>

      <span
        data-column="pullback"
        className={`${PULLBACK_CELL} text-right text-helper-sm tabular-nums text-text-secondary`}
      >
        {percentOrDash(
          candidate.pullback_rate ?? feature(candidate, "PULLBACK_FREQUENCY"),
        )}
      </span>

      <span
        data-column="recovery"
        className={`${RECOVERY_CELL} text-right text-helper-sm tabular-nums text-text-secondary`}
      >
        {percentOrDash(
          candidate.recovery_rate ?? feature(candidate, "RECOVERY_RATE"),
        )}
      </span>

      <span className={`${SCORE_CELL} flex flex-col items-end`}>
        {candidate.final_score == null && candidate.skryptos_score != null ? (
          <Score value={candidate.skryptos_score} testId={`score-${candidate.symbol}`} />
        ) : candidate.final_score == null ? (
          <span className="text-text-muted">—</span>
        ) : (
          <span title={candidate.final_score} className="font-numeric text-body-main tabular-nums text-text-primary">
            {/* Measured to six decimals, read to two. The sixth decimal of a
                score separates nothing a person is choosing between, and it
                pushed the column off the row. The exact figure stays on
                hover. */}
            {money(candidate.final_score)}
          </span>
        )}
      </span>
      <div className="col-span-full px-3">
        <ScoreBreakdown symbol={candidate.symbol} contributions={reading.contributions} />
      </div>
    </li>
  );
}
